import isApplicationUneditable from '@bybeam/platform-lib/application/isApplicationUneditable';
import { ApplicationWithCase } from '@bybeam/platform-lib/application/reapplication';
import useApplication, {
  ApplicationContext,
} from '@platform-ui-common/hooks/contexts/useApplication.js';
import useProgram, { ProgramContext } from '@platform-ui-common/hooks/contexts/useProgram.js';
import useProgress from '@platform-ui-common/hooks/useProgress/useProgress.js';
import { buildApplicationSteps } from '@platform-ui-common/hooks/useProgress/utils.js';
import { ApplicantLayout } from '@platform-ui/application/components/index.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { isProgramOpen } from '@platform-ui/utilities/programs.js';
import { useEffect } from 'react';
import { Redirect, Route, Switch, useParams } from 'react-router';
import { useApplicationProgress } from './hooks/index.js';
import useUserApplications from './hooks/useUserApplications.js';
import {
  ApplicationBankAccount,
  ApplicationQuestion,
  ApplicationReview,
  ApplicationW9,
  ErrorScreen,
  QuestionGroupOverview,
  SectionOverview,
  SubmissionConfirmation,
} from './pages/index.js';
import ApplicantRoutes from './utils/ApplicantRoutes.js';

function ApplicationRoutes(): JSX.Element {
  const application = useApplication();
  const program = useProgram();
  const config = useApplicationConfig();
  const { percentage } = useApplicationProgress();
  const { setOptions, setProgress } = useProgress();
  const { user } = useAuth();

  useEffect(() => {
    const steps = buildApplicationSteps(config, application.answers, application);
    setProgress({ percentage, steps });
    setOptions({
      label: 'Jump to a section:',
      disableSkip: false,
      getRoute: (route: string): string => makeRoute(route, { applicationId: application.id }),
    });
  }, [setOptions, setProgress, application, config, percentage]);

  return (
    <Switch>
      <Route path={ApplicantRoutes.SUBMISSION_CONFIRMATION} component={SubmissionConfirmation} />

      {(isApplicationUneditable(application as ApplicationWithCase) ||
        (!isProgramOpen(program, user.core) && !application.submittedAt)) && (
        <Redirect to={ApplicantRoutes.APPLICATION_PORTAL} />
      )}

      <Route path={ApplicantRoutes.SECTION_OVERVIEW} component={SectionOverview} />
      <Route path={ApplicantRoutes.QUESTION_GROUP_OVERVIEW} component={QuestionGroupOverview} />
      <Route path={ApplicantRoutes.BANK_ACCOUNT} component={ApplicationBankAccount} />
      <Route path={ApplicantRoutes.W9_TAX_FORM_QUESTION} component={ApplicationW9} />
      <Route path={ApplicantRoutes.QUESTION} component={ApplicationQuestion} />
      <Route path={ApplicantRoutes.APPLICATION_REVIEW} component={ApplicationReview} />
      <Route path={ApplicantRoutes.W9_TAX_FORM} component={ApplicationW9} />

      <Route path="*" component={ErrorScreen} />
    </Switch>
  );
}

export default function Routes(): JSX.Element {
  const { applicationId } = useParams<{ applicationId: string }>();
  const applications = useUserApplications();
  const application = applications.find(({ id }) => id === applicationId);

  if (!application) return <Redirect to={ApplicantRoutes.APPLICATION_PORTAL} />;

  return (
    <ProgramContext.Provider value={application.case.program.id}>
      <ApplicationContext.Provider value={application}>
        <ApplicantLayout>
          <ApplicationRoutes />
        </ApplicantLayout>
      </ApplicationContext.Provider>
    </ProgramContext.Provider>
  );
}
